import { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { listCourseProgress, listDeliverableRows } from '../../lib/deliverables';
import type { DeliverableProgress, DeliverableRow } from '../../types/deliverables';
import { DeliverableTable } from './DeliverableTable';
import { Spinner } from '../ui/Spinner';
import { toast } from '../ui/Toaster';
import { ChevronLeft, ClipboardCheck, User, BookOpen } from 'lucide-react';

interface Course { id: string; name: string }

export function DocenteView() {
  const { profile } = useAuth();
  const [courses, setCourses]   = useState<Course[]>([]);
  const [courseId, setCourseId] = useState<string | null>(null);
  const [progress, setProgress] = useState<DeliverableProgress[]>([]);
  const [studentRows, setStudentRows]   = useState<DeliverableRow[] | null>(null);
  const [studentName, setStudentName]   = useState<string>('');
  const [loading, setLoading] = useState(true);

  // Cursos donde es docente
  useEffect(() => {
    if (!profile?.id) return;
    (async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('courses')
        .select('id, name')
        .or(`docente_id.eq.${profile.id},secondary_docente_id.eq.${profile.id}`)
        .eq('status', 'active')
        .order('name', { ascending: true });
      if (error) {
        toast.error('No pude cargar tus cursos');
        setLoading(false);
        return;
      }
      setCourses(data ?? []);
      setLoading(false);
    })();
  }, [profile?.id]);

  // Progreso por alumno
  useEffect(() => {
    if (!courseId) return;
    (async () => {
      setLoading(true);
      const ps = await listCourseProgress(courseId);
      // Completar nombres si vienen vacíos: traemos profiles
      const ids = ps.map((p) => p.student_id);
      if (ids.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, full_name')
          .in('id', ids);
        const nameById = new Map<string, string>((profiles ?? []).map((p: any) => [p.id, p.full_name ?? '']));
        for (const p of ps) {
          if (!p.student_name) p.student_name = nameById.get(p.student_id) ?? null;
        }
      }
      // Orden: por % desc, luego por nombre
      ps.sort((a, b) => (b.percent - a.percent) || (a.student_name ?? '').localeCompare(b.student_name ?? ''));
      setProgress(ps);
      setLoading(false);
    })();
  }, [courseId]);

  const openStudent = async (p: DeliverableProgress) => {
    setLoading(true);
    const rs = await listDeliverableRows(p.deliverable_id);
    setStudentRows(rs);
    setStudentName(p.student_name ?? '—');
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  // ── Vista detallada de un alumno ──
  if (studentRows && courseId) {
    return (
      <div className="space-y-6 p-4 sm:p-6 lg:p-8 overflow-y-auto">
        <button
          type="button"
          onClick={() => { setStudentRows(null); setStudentName(''); }}
          className="inline-flex items-center gap-2 text-cubus-lime hover:underline text-sm"
        >
          <ChevronLeft className="w-4 h-4" />
          Volver a la lista
        </button>
        <h1 className="text-display text-2xl lg:text-3xl font-bold text-white">
          Entregable de <span className="text-cubus-lime">{studentName}</span>
        </h1>
        <p className="text-muted text-sm">Solo lectura — esta vista refleja exactamente lo que ve el alumno.</p>
        <DeliverableTable rows={studentRows} editable={false} />
      </div>
    );
  }

  // ── Lista de cursos ──
  if (!courseId) {
    return (
      <div className="space-y-6 p-4 sm:p-6 lg:p-8 overflow-y-auto">
        <header>
          <h1 className="text-display text-2xl lg:text-3xl font-bold text-white mb-2">Entregables — Mis Cursos</h1>
          <p className="text-muted text-sm">Elegí un curso para ver el progreso de cada alumno.</p>
        </header>
        {courses.length === 0 ? (
          <div className="text-center text-muted py-12">No tenés cursos asignados.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {courses.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setCourseId(c.id)}
                className="glass-surface rounded-xl p-5 border border-white/[0.06] hover:border-cubus-lime/40 transition-all text-left group"
              >
                <div className="flex items-center gap-3 mb-2">
                  <BookOpen className="w-5 h-5 text-cubus-lime/70 group-hover:text-cubus-lime" />
                  <span className="text-[10px] uppercase tracking-[2px] text-white/40 font-semibold">Curso</span>
                </div>
                <h3 className="font-bold text-white group-hover:text-cubus-lime transition-colors">{c.name}</h3>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── Lista de alumnos del curso ──
  const courseName = courses.find((c) => c.id === courseId)?.name ?? '';
  return (
    <div className="space-y-6 p-4 sm:p-6 lg:p-8 overflow-y-auto">
      <button
        type="button"
        onClick={() => setCourseId(null)}
        className="inline-flex items-center gap-2 text-cubus-lime hover:underline text-sm"
      >
        <ChevronLeft className="w-4 h-4" />
        Volver a cursos
      </button>
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-cubus-lime/10 border border-cubus-lime/30 flex items-center justify-center">
          <ClipboardCheck className="w-6 h-6 text-cubus-lime" />
        </div>
        <div>
          <h1 className="text-display text-2xl font-bold text-white">{courseName}</h1>
          <p className="text-muted text-sm">{progress.length} alumno{progress.length === 1 ? '' : 's'} con entregable iniciado</p>
        </div>
      </div>

      {progress.length === 0 ? (
        <div className="text-center text-muted py-12">
          Ningún alumno arrancó su entregable todavía.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {progress.map((p) => (
            <button
              key={p.deliverable_id}
              type="button"
              onClick={() => openStudent(p)}
              className="glass-surface rounded-xl p-4 border border-white/[0.06] hover:border-cubus-lime/40 transition-all text-left flex items-center gap-4"
            >
              <div className="w-10 h-10 rounded-full bg-white/[0.05] flex items-center justify-center flex-shrink-0">
                <User className="w-5 h-5 text-white/60" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-white truncate">{p.student_name || '—'}</div>
                {p.project_name && (
                  <div className="text-xs text-white/50 truncate">{p.project_name}</div>
                )}
                <div className="mt-2 flex items-center gap-2">
                  <div className="flex-1 h-1.5 rounded-full bg-white/[0.05] overflow-hidden">
                    <div className="h-full bg-cubus-lime" style={{ width: `${p.percent}%` }} />
                  </div>
                  <span className="text-xs text-cubus-lime font-semibold w-10 text-right">{p.percent}%</span>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
